import React, { useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import kpService from '../../api/kpService';
import priceService from '../../api/priceService';
import templateService from '../../api/templateService';
import { projectToKPModules, getProjectInfo } from '../utils/projectToKPModules';
import elementService from '../../api/elementService';
import { toast } from 'react-toastify';
import { Canvg } from 'canvg';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ArimoBase64 } from './fonts/Arimo-Regular-normal';
import logoUrl from '../../Assets/Logo_Horizontal.svg';
import { usePricing } from '../../contexts/PricingContext';
import { INITIAL_PPM, MIN_ZOOM_SCALE, STAIRS_COUNTERPART_MAP, OBJECT_TYPES } from '../configuratorConstants';

const getModuleVertices = (module) => {
    const w = module.width;
    const h = module.height;
    const x = module.x;
    const y = module.y;
    const angleRad = ((module.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const localCorners = [
        { x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h },
    ];
    return localCorners.map((corner) => ({
        x: x + (corner.x * cos - corner.y * sin),
        y: y + (corner.x * sin + corner.y * cos),
    }));
};

const CostPanel = ({ totalCost, costBreakdown, projectState, svgRef, setViewTransform, viewTransform, dispatch }) => {
    const { t } = useTranslation();
    const [isGenerating, setIsGenerating] = useState(false);
    const { prices, refetch } = usePricing();
    const [kpSettings, setKpSettings] = useState(null);

    // Fetch KP Settings on mount
    React.useEffect(() => {
        const loadSettings = async () => {
            try {
                // Determine mode based on project state (default to standard/modular)
                // We'll fetch both or just standard for now, but logic inside generation will pick the right one?
                // Actually, let's fetch based on current project type if possible, or just fetch 'standard' and 'frameless'?
                // The backend API takes a key. Let's try to guess or load 'standard' by default.
                // Or better: load the one corresponding to the current project mode
                const isFrameless = projectState?.projectObjects && Object.values(projectState.projectObjects).some(arr => arr.some(o => o.type === 'frameless_wall'));
                const key = isFrameless ? 'frameless' : 'standard';
                const res = await kpService.getSettings(key);
                if (res.data && res.data.success) {
                    let settings = res.data.data;

                    // Parse JSON if needed
                    if (settings && typeof settings.technicalTemplate === 'string') {
                        try {
                            settings.technicalTemplate = JSON.parse(settings.technicalTemplate);
                        } catch (e) {
                            console.error('Failed to parse technicalTemplate JSON', e);
                            settings.technicalTemplate = [];
                        }
                    }

                    setKpSettings(settings);
                }
            } catch (error) {
                console.error('Failed to load KP settings:', error);
            }
        };
        loadSettings();
    }, [projectState]); // Re-load if project type might change (though usually stays same per session)

    const formatPrice = (price) => {
        return new Intl.NumberFormat('uk-UA', { style: 'currency', currency: 'UAH', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    };

    // Проверяет, образуют ли стены замкнутое пространство (комнату)
    const checkIfFramelessProjectIsClosed = (projectState) => {
        if (!projectState || !projectState.projectObjects) return false;

        const epsilon = 0.01;
        const allWalls = [];

        // Собираем все стены со всех этажей
        Object.values(projectState.projectObjects).forEach(floorObjects => {
            if (!Array.isArray(floorObjects)) return;
            floorObjects.forEach(obj => {
                if (obj.type === 'frameless_wall' && obj.startPoint && obj.endPoint) {
                    allWalls.push(obj);
                }
            });
        });

        // Минимум 3 стены для треугольника
        if (allWalls.length < 3) return false;

        // Строим граф смежности
        const adjList = new Map();
        allWalls.forEach(wall => {
            const start = `${wall.startPoint.x.toFixed(2)},${wall.startPoint.y.toFixed(2)}`;
            const end = `${wall.endPoint.x.toFixed(2)},${wall.endPoint.y.toFixed(2)}`;

            if (!adjList.has(start)) adjList.set(start, []);
            if (!adjList.has(end)) adjList.set(end, []);

            adjList.get(start).push(end);
            adjList.get(end).push(start);
        });

        // Поиск циклов (комнат) - упрощенная версия
        const visited = new Set();
        let foundClosedRoom = false;

        const dfs = (node, path) => {
            // Проверяем замыкание цикла - минимум 3 стены (треугольник)
            if (path.length >= 4 && path[0] === node) {
                foundClosedRoom = true;
                return true;
            }

            if (visited.has(node) || path.length > 20) return false;

            visited.add(node);
            const neighbors = adjList.get(node) || [];

            for (const neighbor of neighbors) {
                if (path.includes(neighbor) && neighbor === path[0] && path.length >= 4) {
                    foundClosedRoom = true;
                    return true;
                }
                if (!path.includes(neighbor)) {
                    if (dfs(neighbor, [...path, neighbor])) {
                        return true;
                    }
                }
            }

            visited.delete(node);
            return false;
        };

        // Ищем циклы от каждой точки
        for (const startNode of adjList.keys()) {
            visited.clear();
            dfs(startNode, [startNode]);
            if (foundClosedRoom) break;
        }

        return foundClosedRoom;
    };

    // Проверяет, можно ли генерировать КП
    const canGenerateKP = () => {
        const mode = projectState?.activeMode || 'modular';

        // В безкаркасном режиме проверяем, что проект является замкнутым пространством
        if (mode === 'frameless') {
            return checkIfFramelessProjectIsClosed(projectState);
        }

        // В других режимах всегда можно
        return true;
    };

    const getLogoDataUrl = async () => {
        try {
            const response = await fetch(logoUrl);
            const svgText = await response.text();
            const coloredSvgText = svgText.replace(/fill="white"/g, 'fill="#C71D3B"');

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 444;
            canvas.height = 47;

            const v = await Canvg.from(ctx, coloredSvgText, { fetch: window.fetch.bind(window) });
            await v.render();
            return canvas.toDataURL('image/png');
        } catch (error) {
            console.error("Помилка завантаження логотипу:", error);
            toast.error("Не вдалося завантажити логотип для PDF.");
            return null;
        }
    };

    // Генерирует скриншот для конкретного этажа
    const generateLayoutImageBase64ForFloor = async (floorId, floorName) => {
        if (!svgRef.current) return null;

        // Сохраняем текущий этаж и масштаб
        const originalFloorId = projectState.currentFloorId;
        const originalScale = viewTransform?.scale || INITIAL_PPM;
        const originalX = viewTransform?.x || 0;
        const originalY = viewTransform?.y || 0;

        try {
            // Временно переключаемся на нужный этаж
            if (dispatch && originalFloorId !== floorId) {
                dispatch({ type: 'SWITCH_TO_FLOOR', payload: floorId });
                // Ждем перерендера после переключения этажа
                await new Promise(resolve => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(resolve);
                        });
                    });
                });
            }

            // Временно устанавливаем минимальный масштаб для сетки
            if (setViewTransform) {
                setViewTransform(prev => ({
                    ...prev,
                    scale: MIN_ZOOM_SCALE
                }));
            }

            // Ждем перерендера сетки (используем requestAnimationFrame для ожидания следующего кадра)
            await new Promise(resolve => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(resolve);
                });
            });

            const svgElement = svgRef.current;
            const svgClone = svgElement.cloneNode(true);

            const gridElement = svgClone.querySelector('#grid');
            if (gridElement) gridElement.remove();

            const contentGroup = svgClone.querySelector('g[transform]');
            if (!contentGroup) {
                return null;
            }

            // Получаем объекты только для этого этажа
            const floorObjects = projectState.projectObjects[floorId] || [];
            if (!Array.isArray(floorObjects) || floorObjects.length === 0) {
                console.warn(`  ⚠️ Нет объектов на этаже ${floorName}`);
                return null;
            }

            const mode = projectState.activeMode || 'modular';
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            let hasContent = false;

            // Создаем Set с ID объектов этого этажа для фильтрации SVG элементов
            const floorObjectIds = new Set(floorObjects.map(obj => obj.id));

            // Также собираем ID всех связанных объектов (например, сегментов стен)
            floorObjects.forEach(obj => {
                if (obj.wallSegments) {
                    Object.values(obj.wallSegments).forEach(segment => {
                        if (segment.id) floorObjectIds.add(segment.id);
                    });
                }
                if (obj.segments) {
                    obj.segments.forEach(segment => {
                        if (segment.id) floorObjectIds.add(segment.id);
                    });
                }
                if (obj.perimeterWalls) {
                    obj.perimeterWalls.forEach(wall => {
                        if (wall.id) floorObjectIds.add(wall.id);
                        if (wall.segments) {
                            wall.segments.forEach(seg => {
                                if (seg.id) floorObjectIds.add(seg.id);
                            });
                        }
                    });
                }
                if (obj.internalWalls) {
                    obj.internalWalls.forEach(wall => {
                        if (wall.id) floorObjectIds.add(wall.id);
                        if (wall.segments) {
                            wall.segments.forEach(seg => {
                                if (seg.id) floorObjectIds.add(seg.id);
                            });
                        }
                    });
                }
            });

            if (mode === 'modular') {
                // Для модульного режима - используем модули только этого этажа
                const floorModules = floorObjects.filter(obj => obj.type === 'module');
                if (floorModules.length === 0) {
                    console.warn(`  ⚠️ Нет модулей на этаже ${floorName}`);
                    return null;
                }

                floorModules.forEach(module => {
                    const vertices = getModuleVertices(module);
                    vertices.forEach(v => {
                        minX = Math.min(minX, v.x);
                        minY = Math.min(minY, v.y);
                        maxX = Math.max(maxX, v.x);
                        maxY = Math.max(maxY, v.y);
                        hasContent = true;
                    });
                });
            } else if (mode === 'frameless' || mode === 'frame') {
                // Для безкаркасного/каркасного режима - используем стены только этого этажа
                const walls = floorObjects.filter(obj =>
                    obj.type === 'frameless_wall' ||
                    obj.type === 'frameless_partition' ||
                    obj.type === 'frame_structure' ||
                    obj.type === 'frameless_building'
                );

                if (walls.length === 0) {
                    // Если нет стен, проверяем здания
                    const buildings = floorObjects.filter(obj => obj.type === 'frameless_building');
                    if (buildings.length > 0) {
                        buildings.forEach(building => {
                            const allWalls = [
                                ...(building.perimeterWalls || []),
                                ...(building.internalWalls || [])
                            ];
                            allWalls.forEach(wall => {
                                if (wall.startPoint && wall.endPoint) {
                                    minX = Math.min(minX, wall.startPoint.x, wall.endPoint.x);
                                    minY = Math.min(minY, wall.startPoint.y, wall.endPoint.y);
                                    maxX = Math.max(maxX, wall.startPoint.x, wall.endPoint.x);
                                    maxY = Math.max(maxY, wall.startPoint.y, wall.endPoint.y);
                                    hasContent = true;
                                }
                            });
                        });
                    }
                } else {
                    walls.forEach(wall => {
                        if (wall.startPoint && wall.endPoint) {
                            minX = Math.min(minX, wall.startPoint.x, wall.endPoint.x);
                            minY = Math.min(minY, wall.startPoint.y, wall.endPoint.y);
                            maxX = Math.max(maxX, wall.startPoint.x, wall.endPoint.x);
                            maxY = Math.max(maxY, wall.startPoint.y, wall.endPoint.y);
                            hasContent = true;
                        } else if (wall.x !== undefined && wall.y !== undefined && wall.width && wall.length) {
                            // Для frame_structure
                            minX = Math.min(minX, wall.x - wall.width / 2);
                            minY = Math.min(minY, wall.y - wall.length / 2);
                            maxX = Math.max(maxX, wall.x + wall.width / 2);
                            maxY = Math.max(maxY, wall.y + wall.length / 2);
                            hasContent = true;
                        }
                    });
                }
            }

            if (!hasContent) {
                return null;
            }

            // НЕ фильтруем SVG элементы - используем только viewBox для обрезки
            // Это проще и надежнее, так как viewBox автоматически обрежет все вне границ

            // Используем тот же масштаб, что и для рендеринга (MIN_ZOOM_SCALE), чтобы координаты совпадали
            // После установки scale: MIN_ZOOM_SCALE выше, элементы рендерятся с этим масштабом
            const fixedScale = MIN_ZOOM_SCALE; // Должен совпадать с масштабом рендеринга

            // Вычисляем центр контента в мировых координатах
            const centerX = (minX + maxX) / 2;
            const centerY = (minY + maxY) / 2;

            // Вычисляем размеры контента в мировых координатах
            const contentWidth = maxX - minX;
            const contentHeight = maxY - minY;

            // Убеждаемся, что размеры не нулевые (минимальный размер 1 метр)
            const safeContentWidth = Math.max(contentWidth, 1);
            const safeContentHeight = Math.max(contentHeight, 1);

            // Вычисляем размеры в пикселях с фиксированным масштабом
            const contentWidthPx = safeContentWidth * fixedScale;
            const contentHeightPx = safeContentHeight * fixedScale;

            // Центрируем контент в viewBox
            const padding = 550;
            const viewBoxWidth = Math.max(contentWidthPx + padding * 2, 1000); // Минимальная ширина 1000px
            const viewBoxHeight = Math.max(contentHeightPx + padding * 2, 1000); // Минимальная высота 1000px
            const viewBoxX = centerX * fixedScale - viewBoxWidth / 2;
            const viewBoxY = centerY * fixedScale - viewBoxHeight / 2;

            // Сбрасываем transform у contentGroup, так как мы используем viewBox для позиционирования
            if (contentGroup) {
                contentGroup.setAttribute('transform', 'translate(0, 0)');
            }

            // Устанавливаем viewBox с фиксированным масштабом
            svgClone.setAttribute('viewBox', `${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`);
            svgClone.setAttribute('width', viewBoxWidth);
            svgClone.setAttribute('height', viewBoxHeight);

            // Для canvas используем те же размеры
            const cropWidth = viewBoxWidth;
            const cropHeight = viewBoxHeight;

            const svgString = new XMLSerializer().serializeToString(svgClone);

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const renderScaleFactor = 2;
            canvas.width = cropWidth * renderScaleFactor;
            canvas.height = cropHeight * renderScaleFactor;

            const v = await Canvg.from(ctx, svgString, {
                ignoreMouse: true,
                ignoreAnimation: true,
                scaleWidth: canvas.width,
                scaleHeight: canvas.height
            });
            await v.render();
            const imageData = canvas.toDataURL('image/png');

            return { imageData, floorName };
        } catch (error) {
            console.error(`Помилка створення зображення схеми для ${floorName}:`, error);
            return null;
        } finally {
            // Всегда восстанавливаем оригинальный этаж и масштаб
            if (dispatch && originalFloorId !== floorId) {
                dispatch({ type: 'SWITCH_TO_FLOOR', payload: originalFloorId });
                // Ждем перерендера после переключения этажа (без await, так как это finally)
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // Восстанавливаем масштаб после переключения этажа
                        if (setViewTransform) {
                            setViewTransform({
                                x: originalX,
                                y: originalY,
                                scale: originalScale
                            });
                        }
                    });
                });
            } else {
                // Если этаж не менялся, просто восстанавливаем масштаб
                if (setViewTransform) {
                    setViewTransform({
                        x: originalX,
                        y: originalY,
                        scale: originalScale
                    });
                }
            }
        }
    };

    // Генерирует скриншоты для всех этажей
    const generateLayoutImagesForAllFloors = async () => {
        if (!projectState?.floors || projectState.floors.length === 0) {
            return [];
        }

        // Если этаж один, генерируем скриншот для него
        if (projectState.floors.length === 1) {
            const floor = projectState.floors[0];
            const result = await generateLayoutImageBase64ForFloor(floor.id, floor.name);
            return result ? [{ imageData: result.imageData, floorName: result.floorName }] : [];
        }

        // Если несколько этажей, генерируем для каждого
        const floorImages = [];
        for (const floor of projectState.floors) {
            const result = await generateLayoutImageBase64ForFloor(floor.id, floor.name);
            if (result) {
                floorImages.push(result);
            }
        }
        return floorImages;
    };

    // Старая функция для обратной совместимости (используется для одного этажа)
    const generateLayoutImageBase64 = async () => {
        const results = await generateLayoutImagesForAllFloors();
        if (results.length === 0) return null;
        // Если один этаж, возвращаем просто imageData
        if (results.length === 1) return results[0].imageData;
        // Если несколько этажей, возвращаем первый (для обратной совместимости)
        return results[0].imageData;
    };

    const processTemplateRows = (template, context) => {
        if (!Array.isArray(template)) return [];
        return template.map(row => {
            if (row.isHeader) {
                return [{ content: row.param, colSpan: 2, styles: { fillColor: [243, 244, 246], fontStyle: 'bold', halign: 'center' } }];
            }
            let val = row.value || '';
            val = val.replace(/\{\{(\w+)\}\}/g, (m, k) => context[k] !== undefined ? context[k] : m);
            val = val.replace(/<br>/g, '\n');
            val = val.replace(/\\n/g, '\n');
            return [row.param, val];
        });
    };

    const handleGenerateKP = async () => {
        if (isGenerating) return;
        setIsGenerating(true);

        // Получаем свежие цены напрямую из API
        const freshPricesResponse = await priceService.getPublicPrices();
        const currentPrices = freshPricesResponse.data || { MODULES: {}, ELEMENTS: {} };

        // Добавляем цены кастомных элементов (сантехника и т.д.)
        try {
            const customElementsResponse = await elementService.getElementsForConfigurator();
            const customElements = customElementsResponse.data || [];
            customElements.forEach(element => {
                if (element.elementCode && element.price) {
                    currentPrices.ELEMENTS[element.elementCode] = parseFloat(element.price);
                }
            });
        } catch (error) {
            console.error('Error fetching custom elements prices:', error);
        }

        // Завантажуємо шаблони для отримання цін за templateId
        const templatesResponse = await templateService.getPublicTemplates();
        const freshTemplates = (templatesResponse.data || []).map(tpl => ({
            ...tpl,
            config: typeof tpl.config === 'string' ? JSON.parse(tpl.config) : tpl.config,
        }));

        // Створюємо мапу templateId -> price для швидкого доступу
        const templatePricesById = {};
        freshTemplates.forEach(template => {
            const templateId = template.config?.id;
            if (templateId && template.price !== null && template.price !== undefined) {
                templatePricesById[templateId] = parseFloat(template.price);
            }
        });

        try {
            const mode = projectState.activeMode || 'modular';

            let modulesForKP = null;
            let kpData = null;
            const projectInfo = getProjectInfo(projectState);

            // ======== МОДУЛЬНИЙ РЕЖИМ ========
            if (mode === 'modular') {
                modulesForKP = projectToKPModules(projectState);
                if (modulesForKP.length === 0) {
                    toast.warning('В проекті немає модулів для генерації КП');
                    setIsGenerating(false);
                    return;
                }

                const response = await kpService.calculateKP(modulesForKP, projectInfo, mode);
                kpData = response.data.data;

                console.log('📦 [CostPanel] Данные получены с сервера:');
                console.log('  kpData:', kpData);
                console.log('  furnitureDescription:', kpData.furnitureDescription);
                console.log('  furnitureDescription type:', typeof kpData.furnitureDescription);
                console.log('  furnitureDescription length:', kpData.furnitureDescription ? kpData.furnitureDescription.length : 0);
                console.log('  modulesForKP:', modulesForKP);
                console.log('  Модули кухни:', modulesForKP.filter(m => m.subtype === 'kitchen'));
                console.log('  Модули пральни:', modulesForKP.filter(m => m.subtype === 'laundry'));

                // Продовжуємо виконання коду для модульного режиму (далі в файлі)
            }

            // ======== КАРКАСНИЙ/БЕЗКАРКАСНИЙ РЕЖИМ ========
            else if (mode === 'frame' || mode === 'frameless') {
                // Перевіряємо чи є стіни
                const hasWalls = Object.values(projectState.projectObjects || {}).some(floorObjects =>
                    Array.isArray(floorObjects) && floorObjects.some(obj =>
                        obj.type === 'frameless_wall' || obj.type === 'frame_structure'
                    )
                );

                if (!hasWalls) {
                    toast.warning('В проекті немає стін для генерації КП');
                    setIsGenerating(false);
                    return;
                }

                // Для безкаркасного режима проверяем, что проект является замкнутым пространством
                if (mode === 'frameless') {
                    const isClosed = checkIfFramelessProjectIsClosed(projectState);
                    if (!isClosed) {
                        toast.error('Неможливо створити КП: проект не є закритою кімнатою. Стіни повинні утворювати замкнене простір.');
                        setIsGenerating(false);
                        return;
                    }
                }

                const response = await kpService.calculateKP(null, projectInfo, mode, projectState);
                kpData = response.data.data;

                // Для безкаркасного режима проверяем, что есть замкнутые комнаты
                if (mode === 'frameless' && (!kpData.roomsCount || kpData.roomsCount === 0)) {
                    toast.error('Неможливо створити КП: проект не є закритою кімнатою. Стіни повинні утворювати замкнене простір.');
                    setIsGenerating(false);
                    return;
                }

                // ======== ПОВНОЦІННЕ КП ДЛЯ БЕЗКАРКАСНОГО/КАРКАСНОГО ========
                const pdf = new jsPDF('p', 'mm', 'a4');
                pdf.addFileToVFS('Arimo-Regular.ttf', ArimoBase64);
                pdf.addFont('Arimo-Regular.ttf', 'Arimo', 'normal');
                pdf.setFont('Arimo', 'normal');

                // ========== ТИТУЛЬНА СТОРІНКА ==========
                let y = 15;
                const pageHeight = pdf.internal.pageSize.getHeight();
                const leftMargin = 15;

                const logoDataUrl = await getLogoDataUrl();
                if (logoDataUrl) {
                    const logoProps = pdf.getImageProperties(logoDataUrl);
                    const logoWidth = 80;
                    const logoHeight = (logoProps.height * logoWidth) / logoProps.width;
                    const logoY = (pageHeight - logoHeight) / 2 - 30;
                    pdf.addImage(logoDataUrl, 'PNG', (pdf.internal.pageSize.getWidth() - logoWidth) / 2, logoY, logoWidth, logoHeight);
                    y = logoY + logoHeight + 30;
                } else {
                    y = pageHeight / 2;
                }

                pdf.setFont('Arimo', 'normal');
                pdf.setFontSize(32);
                pdf.setTextColor(44, 62, 80);
                pdf.text('Комерційна пропозиція', pdf.internal.pageSize.getWidth() / 2, y, { align: 'center' });

                // ========== СТОРІНКА З ДЕТАЛЯМИ ==========
                pdf.addPage();
                y = 20;

                pdf.setFont('Arimo', 'normal');
                pdf.setFontSize(13);
                pdf.setTextColor(100, 116, 139);
                pdf.text(`Дата: ${kpData.date}`, pdf.internal.pageSize.getWidth() - leftMargin, y, { align: 'right' });
                y += 15;

                const detailTableBody = [];

                // Для безкаркасного режима: рахуємо вартість по площі (клеткам)
                let wallsTotalCost = 0;
                if (mode === 'frameless' && kpData.roomsCount > 0) {
                    const FRAMELESS_GRID_CELL_SIZE_M = 1.0;
                    // Получаем цену за квадратный метр из прайса (или используем значение по умолчанию)
                    const DEFAULT_FRAMELESS_CELL_PRICE = 44436;
                    const framelessSquareMeterPrice = currentPrices.ELEMENTS?.[OBJECT_TYPES.FRAMELESS_SQUARE_METER_PRICE] || DEFAULT_FRAMELESS_CELL_PRICE;

                    // Собираем все безкаркасные стены для расчета площади комнат
                    const allFramelessWalls = [];
                    for (const floorId in projectState.projectObjects) {
                        const floorObjects = projectState.projectObjects[floorId];
                        allFramelessWalls.push(...floorObjects.filter(obj =>
                            obj.type === 'frameless_wall'
                        ));
                    }

                    // Находим все замкнутые циклы (комнаты) - нужно импортировать функцию
                    // Используем ту же логику, что и в useCostCalculation
                    const findAllClosedLoops = (walls) => {
                        const loops = [];
                        const epsilon = 0.001;
                        const distance = (p1, p2) => Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

                        const getNextPoint = (wall, fromPoint) => {
                            const distToStart = distance(fromPoint, wall.startPoint);
                            const distToEnd = distance(fromPoint, wall.endPoint);
                            if (distToStart < epsilon) return wall.endPoint;
                            if (distToEnd < epsilon) return wall.startPoint;
                            return null;
                        };

                        for (let i = 0; i < walls.length; i++) {
                            const startWall = walls[i];
                            for (const startPoint of [startWall.startPoint, startWall.endPoint]) {
                                const visited = new Set([startWall.id]);
                                const path = [startWall];
                                const currentPoint = getNextPoint(startWall, startPoint);
                                if (!currentPoint) continue;

                                const dfs = (currentWall, currentEnd) => {
                                    if (path.length >= 4) {
                                        const distToStart = distance(currentEnd, startPoint);
                                        if (distToStart < epsilon) {
                                            const loop = [...path];
                                            const loopKey = loop.map(w => w.id).sort().join('|');
                                            const isDuplicate = loops.some(existingLoop => {
                                                const existingKey = existingLoop.map(w => w.id).sort().join('|');
                                                return existingKey === loopKey;
                                            });
                                            if (!isDuplicate) {
                                                loops.push(loop);
                                            }
                                            return;
                                        }
                                    }
                                    if (path.length >= 10) return;

                                    for (const nextWall of walls) {
                                        if (visited.has(nextWall.id)) continue;
                                        const nextPoint = getNextPoint(nextWall, currentEnd);
                                        if (nextPoint) {
                                            visited.add(nextWall.id);
                                            path.push(nextWall);
                                            dfs(nextWall, nextPoint);
                                            path.pop();
                                            visited.delete(nextWall.id);
                                        }
                                    }
                                };
                                dfs(startWall, currentPoint);
                            }
                        }

                        const loopsWithBounds = loops.map(loop => {
                            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                            loop.forEach(wall => {
                                minX = Math.min(minX, wall.startPoint.x, wall.endPoint.x);
                                minY = Math.min(minY, wall.startPoint.y, wall.endPoint.y);
                                maxX = Math.max(maxX, wall.startPoint.x, wall.endPoint.x);
                                maxY = Math.max(maxY, wall.startPoint.y, wall.endPoint.y);
                            });
                            const area = (maxX - minX) * (maxY - minY);
                            return { loop, area };
                        });

                        const minimalLoops = loopsWithBounds.filter((loopData, index) => {
                            const { loop, minX, minY, maxX, maxY, area } = loopData;
                            for (let j = 0; j < loopsWithBounds.length; j++) {
                                if (j === index) continue;
                                const other = loopsWithBounds[j];
                                if (other.area < area) {
                                    const isInside =
                                        other.minX >= minX - 0.01 &&
                                        other.maxX <= maxX + 0.01 &&
                                        other.minY >= minY - 0.01 &&
                                        other.maxY <= maxY + 0.01;
                                    if (isInside) return false;
                                }
                            }
                            return true;
                        });

                        return minimalLoops.map(data => ({ loop: data.loop, area: data.area }));
                    };

                    const closedLoops = findAllClosedLoops(allFramelessWalls);

                    // Считаем стоимость по площади (клеткам)
                    closedLoops.forEach((roomData) => {
                        const { area } = roomData;
                        const cellsCount = area / (FRAMELESS_GRID_CELL_SIZE_M * FRAMELESS_GRID_CELL_SIZE_M);
                        const roomCost = cellsCount * framelessSquareMeterPrice;
                        wallsTotalCost += roomCost;
                    });
                } else {
                    // Для других режимов используем старую логику
                    const FRAMELESS_GRID_CELL_SIZE_M = 1.0;
                    const framelessWallPrice = currentPrices.ELEMENTS['internal_wall_tool'] || 0;
                    const wallsSegmentsCount = kpData.elementCounts?.walls?.totalCount || 0;
                    wallsTotalCost = wallsSegmentsCount * framelessWallPrice;
                }

                // Додаємо інформацію про приміщення/стіни замість модулів
                // Не показуємо рядок "Приміщення" якщо їх 0
                if (kpData.roomsCount > 0) {
                    const roomInfo = `Приміщення ${kpData.roomDimensionsText || ''}`;
                    detailTableBody.push([roomInfo, formatNumber(wallsTotalCost)]); // Ціна стін включена в приміщення
                }

                // Додаємо елементи з elementCounts
                const elementMapping = {
                    'window': { name: 'Вікно стандартне', category: 'windows', field: 'small' },
                    'panoramic_window': { name: 'Вікно панорамне', category: 'windows', field: 'large' },
                    'door': { name: 'Двері', category: 'doors', field: 'doors' },
                    'gate': { name: 'Ворота', category: 'gates', field: 'gates' },
                    'outlet': { name: 'Розетка електрична одинарна', category: 'sockets', field: 'single' },
                    'outlet_double': { name: 'Розетка електрична подвійна', category: 'sockets', field: 'double' },
                    'switch': { name: 'Вимикач освітлення', category: 'lighting', field: 'switches' },
                    'light_ceiling': { name: 'Світильник LED 36W', category: 'lighting', field: 'fixtures' },
                    'radiator': { name: 'Обігрівач електричний', category: 'climate', field: 'heaters' },
                    'ac': { name: 'Кондиціонер', category: 'climate', field: 'airConditioners' },
                    'roller_shutter': { name: 'Ролета', category: 'climate', field: 'windowBlinds' },
                    'hand_basin': { name: 'Умивальник з підводкою', category: 'plumbing', field: 'sinks' },
                    'toilet': { name: 'Унітаз керамічний', category: 'plumbing', field: 'toilets' },
                    'water_heater': { name: 'Бойлер електричний', category: 'plumbing', field: 'waterHeaters' },
                    'shower': { name: 'Душова кабіна', category: 'plumbing', field: 'showers' },
                    'urinal': { name: 'Пісуар керамічний', category: 'plumbing', field: 'urinals' },
                    'partition': { name: 'Перегородка сантехнічна', category: 'plumbing', field: 'partitions' },
                    'shower_drain': { name: 'Душовий трап (злив)', category: 'plumbing', field: 'showerDrains' },
                    'bed': { name: 'Ліжко металеве 1900×800', category: 'furniture', field: 'beds' },
                    'desk': { name: 'Стіл письмовий', category: 'furniture', field: 'desks' },
                    'chair': { name: 'Стілець офісний', category: 'furniture', field: 'chairs' }
                };

                Object.entries(elementMapping).forEach(([elemKey, elemInfo]) => {
                    // Для gates используем прямую проверку kpData.gates, так как это отдельное поле
                    let count = 0;
                    if (elemKey === 'gate') {
                        count = kpData.gates || kpData.elementCounts?.gates || 0;
                    } else if (elemKey === 'door') {
                        // Для дверей используем общее количество из kpData.doors
                        count = kpData.doors || 0;
                    } else {
                        count = kpData.elementCounts?.[elemInfo.category]?.[elemInfo.field] || 0;
                    }
                    if (count > 0) {
                        const elementPrice = currentPrices.ELEMENTS[elemKey] || 0;
                        const totalPrice = elementPrice * count;

                        // Специальный формат для ворот
                        if (elemKey === 'gate') {
                            detailTableBody.push([
                                `${elemInfo.name} (${count} шт.)`,
                                formatNumber(totalPrice)
                            ]);
                        }
                        // Специальный формат для панорамных окон
                        else if (elemKey === 'panoramic_window') {
                            detailTableBody.push([
                                `${elemInfo.name} (${count} шт.)`,
                                formatNumber(totalPrice)
                            ]);
                        }
                        // Стандартный формат для остальных элементов
                        else {
                            detailTableBody.push([
                                `${elemInfo.name} (${count} шт.)`,
                                formatNumber(totalPrice)
                            ]);
                        }
                    }
                });

                // Таблиця деталей
                autoTable(pdf, {
                    startY: y,
                    head: [['Найменування', { content: 'Вартість, грн', styles: { halign: 'right' } }]],
                    body: detailTableBody,
                    theme: 'striped',
                    headStyles: { fillColor: [199, 29, 59], font: 'Arimo', fontStyle: 'normal', fontSize: 14, cellPadding: 6 },
                    bodyStyles: {
                        font: 'Arimo',
                        fontStyle: 'normal',
                        fontSize: 12,
                        cellPadding: 5
                    },
                    columnStyles: {
                        0: { cellWidth: 120, font: 'Arimo', fontStyle: 'normal' },
                        1: { cellWidth: 60, halign: 'right', font: 'Arimo', fontStyle: 'normal' }
                    },
                    styles: { font: 'Arimo', fontStyle: 'normal' },
                    didDrawPage: (data) => { y = data.cursor.y; }
                });
                y += 10;

                // Підсумкова таблиця
                const buildingDescription = mode === 'frame'
                    ? `Будівля каркасна\n${kpData.buildingDimensions}`
                    : `Будівля безкаркасна\n${kpData.buildingDimensions}`;

                const summaryTableBody = [[
                    buildingDescription,
                    formatNumber(kpData.totalPrice)
                ]];

                autoTable(pdf, {
                    startY: y,
                    head: [['Найменування', { content: 'Вартість з ПДВ, грн', styles: { halign: 'right' } }]],
                    body: summaryTableBody,
                    theme: 'striped',
                    headStyles: { fillColor: [199, 29, 59], font: 'Arimo', fontStyle: 'normal', fontSize: 14, cellPadding: 6 },
                    bodyStyles: {
                        font: 'Arimo',
                        fontStyle: 'normal',
                        fontSize: 13,
                        cellPadding: 8,
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 120, font: 'Arimo', fontStyle: 'normal' },
                        1: { cellWidth: 60, halign: 'right', font: 'Arimo', fontStyle: 'normal', textColor: [199, 29, 59], fontStyle: 'bold', fontSize: 15 }
                    },
                    styles: { font: 'Arimo', fontStyle: 'normal' },
                    didDrawPage: (data) => { y = data.cursor.y; }
                });

                // ========== ТЕХНІЧНА ЧАСТИНА ==========
                pdf.addPage();
                y = 20;

                pdf.setFont('Arimo', 'normal');
                pdf.setFontSize(18);
                pdf.setTextColor(44, 62, 80);
                pdf.text('Технічні характеристики', pdf.internal.pageSize.getWidth() / 2, y, { align: 'center' });
                y += 15;

                let technicalData = [];

                if (mode === 'frameless') {
                    // Детальні технічні характеристики для безкаркасного режиму
                    const dim = kpData.roomDimensions && kpData.roomDimensions.length > 0
                        ? kpData.roomDimensions[0]
                        : { width: 0, height: 0 };

                    const PANEL_SIZE = 1.22;
                    const widthInPanels = Math.round(dim.width / PANEL_SIZE);
                    const heightInPanels = Math.round(dim.height / PANEL_SIZE);
                    const actualWidthMM = Math.round(widthInPanels * PANEL_SIZE * 1000);
                    const actualHeightMM = Math.round(heightInPanels * PANEL_SIZE * 1000);

                    const totalDoorsCount = kpData.doors || 0;

                    // Проверяем наличие санитарных элементов
                    const hasPlumbingElements = kpData.elementCounts?.plumbing && (
                        (kpData.elementCounts.plumbing.sinks || 0) > 0 ||
                        (kpData.elementCounts.plumbing.toilets || 0) > 0 ||
                        (kpData.elementCounts.plumbing.waterHeaters || 0) > 0 ||
                        (kpData.elementCounts.plumbing.showers || 0) > 0 ||
                        (kpData.elementCounts.plumbing.urinals || 0) > 0 ||
                        (kpData.elementCounts.plumbing.partitions || 0) > 0 ||
                        (kpData.elementCounts.plumbing.showerDrains || 0) > 0
                    );

                    // Формируем текст про внутренние перегородки
                    let partitionsText = 'Панелі металеві з утеплювачем 100 мм із пінополіізоціанурату, зовнішня та внутрішня сторона з сталевого профільного або гладкого листа, товщина металу не менше ніж 0,45 мм. На вибір Замовника.';
                    if (hasPlumbingElements) {
                        partitionsText += ' Санвузол – панелі металеві з утеплювачем 100 мм, зовнішня та внутрішня сторона з сталевого профільного або гладкого листа, товщиною 0,45 мм, зовні колір на вибір Замовника, зсередини колір на вибір Замовника.';
                    }

                    technicalData = [
                        ['1. Підстава для розробки', 'Комерційна пропозиція'],
                        ['2. Вид споруди', 'Збірно-розбірна'],
                        ['3. Виробник', 'ТОВ «РЕД БУД»'],
                        ['4. Інженерні вишукування', 'В обсязі який необхідно'],
                        ['5. Призначення об\'єкта', '1-но поверхова споруда'],

                        // Конструктивні рішення
                        [{ content: '6. Конструктивні рішення', colSpan: 2, styles: { fillColor: [243, 244, 246], fontStyle: 'bold', halign: 'center' } }],
                        ['6.1. Каркас', 'Відсутній'],
                        ['6.2. Дах', 'Односкатний/двоскатний, покрівельні сендвіч-панелі товщиною 140 мм, ззовні профільований лист товщиною 0,45 мм з полімерним покриттям, колір по RAL погоджується Замовником, наповнення з PIR матеріалу, зсередини профільований лист товщиною 0,45 мм з полімерним покриттям, колір на вибір замовника'],
                        ['6.4. Кріпильні елементи та армування стін, перестінок, даху', 'Гнутий пресом сталевий оцинкованих П-подібний профіль, товщина металу не менше ніж 2 мм; перфоровані кріплення з товщиною металу не менше ніж 2 мм.'],
                        ['6.6. Стіни зовнішні', 'Панелі металеві з утеплювачем 100 мм із пінополіізоціанурату, зовнішня та внутрішня сторона з сталевого профільного або гладкого листа, товщина металу не менше ніж 0,45 мм. Колір на вибір Замовника.'],
                        ['6.7. Стеля', 'Набірні стелі типу Армстронг з використанням вологостійких матеріалів у вологих приміщеннях.'],
                        ['6.8. Основа', 'Залізобетонна гідроізольована та утеплена фундаментна плита, з відмосткою по периметру споруди.'],
                        ['6.9. Підлога', 'Покриття підлоги комерційний лінолеум товщиною 2 мм, по периметру плінтус в колір.'],
                        ['6.10. Двері', totalDoorsCount > 0 ? `Двері зовнішні - відкриття на зовнішню сторону, правосторонні/лівосторонні, стальний короб з ущільнювачем по периметру дверей, двостороння обшивка, пофарбованим сталевим листом. Зовнішні розміри: 960 х 2050 мм фарбовані в колір на вибір Замовника. Двері внутрішні - металопластикові, глухі, світловий пройм 800х2000 мм, одностулкові, імпост на висоті 800 мм, обидва пройми сендвіч-панель, врізаний замок, натискна ручка, доводчик, відбійник, колір на вибір Замовника. Загальна кількість ${totalDoorsCount} шт.` : 'Не встановлено.'],
                        ['6.10.1. Гараж', (() => {
                            const gatesCount = kpData.gates || kpData.elementCounts?.gates || 0;
                            return gatesCount > 0
                                ? `(${gatesCount} шт.) ворота ролетні 2500х2200 мм, фарбовані в колір на вибір Замовника`
                                : 'Не встановлено.';
                        })()],
                        ['6.12. Вікна', (() => {
                            const standardWindowsCount = kpData.elementCounts?.windows?.small || 0;
                            return standardWindowsCount > 0
                                ? `Металопластикові, похило-поворотні, 800х1200(h) мм, подвійний склопакет 3-камерний, москітні сітки, колір на вибір Замовника. Загальна кількість ${standardWindowsCount} шт.`
                                : 'Не встановлено.';
                        })()],
                        ['6.12.1. Вікна панорамні', (() => {
                            const panoramicWindowsCount = kpData.elementCounts?.windows?.large || 0;
                            return panoramicWindowsCount > 0
                                ? `Вікно панорамне (${panoramicWindowsCount} шт.). Розмір панорамних вікон погоджується з Замовником`
                                : 'Не встановлено.';
                        })()],
                        ['6.13. Захисні ролети на вікна', `Колір на вибір Замовника. ${kpData.elementCounts?.climate?.windowBlinds > 0 ? `Загальна кількість ${kpData.elementCounts.climate.windowBlinds} шт.` : 'Не встановлено.'}`],
                        ['6.14. Внутрішні перегородки', partitionsText],
                        ['6.15. Обрамлення (декоративні планки)', 'Зовнішнє – гнуті планки з гладкого металевого листа 0,45 мм, з полімерним покриттям. Внутрішнє – гнуті планки з гладкого металевого листа не менше ніж 0,45 мм. Колір на вибір Замовника.'],

                        // Об'ємно-планувальні рішення
                        [{ content: '7. Об\'ємно-планувальні рішення', colSpan: 2, styles: { fillColor: [243, 244, 246], fontStyle: 'bold', halign: 'center' } }],
                        ['7.1. Габаритні розміри довжина × ширина, мм', `${actualWidthMM}±50 × ${actualHeightMM}±50`],
                        ['7.2. Поверховість споруди', 'Один поверх.'],
                        ['7.3. Висота від підлоги до стелі, мм', 'Становить, не менше ніж 2500±100'],
                        ['7.4. Кліматичні умови експлуатації', 'Температура зовнішнього повітря, °С: -30±5°С...+40±5°С; Відносна вологість повітря при температурі +20°С, не більше 95%.'],

                        // Обладнання
                        [{ content: '8. Обладнання', colSpan: 2, styles: { fillColor: [243, 244, 246], fontStyle: 'bold', halign: 'center' } }],
                        ['8.1. Розетка подвійна 220В вологозахищена', `${kpData.elementCounts?.sockets?.double || 0} шт.`],
                        ['8.2. Розетка одинарна 220В вологозахищена', `${kpData.elementCounts?.sockets?.single || 0} шт.`],
                        ['8.3. Вимикачі подвійні', `${kpData.elementCounts?.lighting?.switches || 0} шт.`],
                        ['8.4. Вимикачі одинарні', `${kpData.elementCounts?.lighting?.switches || 0} шт.`],
                        ['8.5. Кондиціонер (спліт-система інверторного)', `${kpData.elementCounts?.climate?.airConditioners || 0} шт.`],
                        ['8.6. Унітаз компакт з бачком і сидінням дюропласт', `${kpData.elementCounts?.plumbing?.toilets || 0} шт.`],
                        ['8.7. Душова кабіна з піддоном та шторою 1000 х 1000 мм', `${kpData.elementCounts?.plumbing?.showers || 0} шт.`],
                        ['8.8. Умивальник із змішувачем та сифоном', `${kpData.elementCounts?.plumbing?.sinks || 0} шт.`],

                        // Інші вимоги
                        [{ content: '9. Інші вимоги', colSpan: 2, styles: { fillColor: [243, 244, 246], fontStyle: 'bold', halign: 'center' } }],
                        ['9.1. Електромережі', 'Кабельна продукція прокладена в лотках, гофрованих трубах або кабельних каналах. Головний розподільний щит IP54 (захищений від вологи), з місцем під лічильник. Кабель на розеткові групи - мідний провід з подвійною ізоляцією ВВГнг 3х2.5 мм². Кабель на групи освітлення - мідний провід з подвійною ізоляцією ВВГнг 3х1.5 мм². Усі рішення відповідають ДСТУ Б В.2.2-33:2011, ПУЕ.'],
                        ['9.2. Заземлення', 'Виконане відповідно до вимог керівних документів з відповідними замірами опору.'],
                        ['9.3. Блискавкозахист', 'Виконаний відповідно до вимог керівних документів з відповідними замірами опору. Блискавкоприймач висотою до 2,0 м, з провідника діаметром 10 мм, який приєднується до контуру заземлення.'],
                        ['9.4. Холодне водопостачання', 'ППР труби прокладені відкритим способом.'],
                        ['9.5. Гаряче водопостачання', 'ППР труби прокладені відкритим способом.'],
                        ['9.6. Каналізація', 'ПВХ труби 50 мм, 110 мм прокладені відкритим способом.'],
                        ['9.7. Відведення дощових та талих вод з покрівлі споруди', 'ПВХ труби, колір погоджується Замовником.'],
                        ['9.8. Гарантійний термін експлуатації', 'Гарантійний термін експлуатації споруди, конструкцій, деталей, вузлів, обладнання, санітарно-технічних приладів, приладів опалення, вентиляції, водопостачання не менше 24 місяців. Постачальник несе повну відповідальність за збереження несучої спроможності конструктивних елементів п. 6 на весь час експлуатації.'],
                        ['9.9. Технічна документація', 'Паспорт на споруду, керівництво з експлуатації споруди, сертифікат відповідності на споруду, виконавчі схеми на усі інженерні мережі та елементи безпеки.'],
                        ['9.10. Поставка та монтаж', 'За домовленістю.']
                    ];
                } else {
                    // Спрощені характеристики для каркасного режиму
                    technicalData = [
                        ['Несуча конструкція', mode === 'frame' ? 'Металокаркас' : 'Сендвіч-панелі'],
                        ['Зовнішня обшивка', 'Сталевий лист з полімерним покриттям, товщина 0.45-0.5 мм'],
                        ['Утеплення', 'Мінеральна вата, товщина 100-150 мм'],
                        ['Внутрішня обшивка', 'Ламінований ДСП або OSB, товщина 10-16 мм'],
                        ['Підлога', 'Багатошарова конструкція: OSB, утеплення, фінішне покриття (лінолеум)'],
                        ['Покрівля', 'Оцинкований сталевий лист, подвійний фальц, гідроізоляція, утеплення'],
                        ['Вікна', kpData.windowsDescription || 'Відсутні.'],
                        ['Двері', kpData.doorsDescription || 'Відсутні.'],
                        ['Електрика (розетки)', kpData.socketsDescription || 'Відсутня.'],
                        ['Електрика (освітлення)', kpData.lightingDescription || 'Відсутнє.'],
                        ['Клімат', kpData.climateDescription || 'Відсутній.'],
                        ['Сантехніка', kpData.plumbingDescription || 'Відсутня.'],
                        ['Меблювання', kpData.furnitureDescription || 'Відсутнє.']
                    ];

                    if (kpSettings && kpSettings.technicalTemplate && kpSettings.technicalTemplate.length > 0) {
                        // Для стандартного режиму також намагаємось використати шаблон
                        // Доповнюємо kpData значеннями за замовчуванням
                        const templateContext = {
                            ...kpData,
                            frameStructure: mode === 'frame' ? 'Металокаркас' : 'Сендвіч-панелі',
                            // Add headers/descriptions if needed by template
                        };
                        technicalData = processTemplateRows(kpSettings.technicalTemplate, templateContext);
                    }
                }

                autoTable(pdf, {
                    startY: y,
                    head: [['Характеристика', 'Опис']],
                    body: technicalData,
                    theme: 'striped',
                    headStyles: {
                        fillColor: [199, 29, 59],
                        font: 'Arimo',
                        fontStyle: 'normal',
                        fontSize: 13,
                        cellPadding: 6
                    },
                    bodyStyles: {
                        font: 'Arimo',
                        fontStyle: 'normal',
                        fontSize: 10,
                        cellPadding: 5
                    },
                    columnStyles: {
                        0: { cellWidth: 60, fontStyle: 'bold' },
                        1: { cellWidth: 120 }
                    },
                    styles: { font: 'Arimo', fontStyle: 'normal' }
                });

                // ========== ПЛАН-СХЕМА ==========
                const floorImages = await generateLayoutImagesForAllFloors();
                if (floorImages.length > 0) {
                    for (const { imageData, floorName } of floorImages) {
                        if (!imageData) continue;

                        pdf.addPage();
                        const topMargin = 35;
                        const bottomMargin = 35;
                        const pageW = pdf.internal.pageSize.getWidth();
                        const pageH = pdf.internal.pageSize.getHeight();

                        pdf.setFont('Arimo', 'normal');
                        pdf.setFontSize(16);
                        pdf.setTextColor(44, 62, 80);
                        const title = floorImages.length > 1 ? `План-схема будівлі - ${floorName}` : 'План-схема будівлі';
                        pdf.text(title, pageW / 2, 25, { align: 'center' });

                        const availableWidth = pageW - leftMargin * 2;
                        const availableHeight = pageH - topMargin - bottomMargin;

                        const imgProps = pdf.getImageProperties(imageData);
                        const imageAspectRatio = imgProps.width / imgProps.height;
                        const availableSpaceAspectRatio = availableWidth / availableHeight;

                        let finalImgWidth, finalImgHeight;
                        if (imageAspectRatio > availableSpaceAspectRatio) {
                            finalImgWidth = availableWidth;
                            finalImgHeight = finalImgWidth / imageAspectRatio;
                        } else {
                            finalImgHeight = availableHeight;
                            finalImgWidth = finalImgHeight * imageAspectRatio;
                        }

                        const centerX = (pageW - finalImgWidth) / 2;
                        const centerY = topMargin + (availableHeight - finalImgHeight) / 2;

                        pdf.addImage(imageData, 'PNG', centerX, centerY, finalImgWidth, finalImgHeight);
                    }
                }

                pdf.save(`КП_${kpData.projectName.replace(/\s/g, '_')}_${kpData.date}.pdf`);
                toast.success('PDF успішно згенеровано!');
                setIsGenerating(false);
                return;
            }

            // ======== НЕВІДОМИЙ РЕЖИМ ========
            else {
                toast.error(`Невідомий режим: ${mode}`);
                setIsGenerating(false);
                return;
            }

            // Далі йде КОД ТІЛЬКИ ДЛЯ МОДУЛЬНОГО РЕЖИМУ:
            // Перевіряємо, що ми дійсно в модульному режимі
            if (mode !== 'modular') {
                console.error('❌ ПОМИЛКА: Код для модульного режиму виконується для режиму:', mode);
                setIsGenerating(false);
                return;
            }

            if (!kpData) {
                console.error('❌ ПОМИЛКА: kpData відсутній для модульного режиму');
                toast.error('Помилка генерації КП: дані не отримані');
                setIsGenerating(false);
                return;
            }

            const pdf = new jsPDF('p', 'mm', 'a4');
            pdf.addFileToVFS('Arimo-Regular.ttf', ArimoBase64);
            pdf.addFont('Arimo-Regular.ttf', 'Arimo', 'normal');
            pdf.setFont('Arimo', 'normal');

            let y = 15;
            const pageHeight = pdf.internal.pageSize.getHeight();
            const leftMargin = 15;

            const logoDataUrl = await getLogoDataUrl();
            if (logoDataUrl) {
                const logoProps = pdf.getImageProperties(logoDataUrl);
                const logoWidth = 80;
                const logoHeight = (logoProps.height * logoWidth) / logoProps.width;
                const logoY = (pageHeight - logoHeight) / 2 - 30;
                pdf.addImage(logoDataUrl, 'PNG', (pdf.internal.pageSize.getWidth() - logoWidth) / 2, logoY, logoWidth, logoHeight);
                y = logoY + logoHeight + 30;
            } else {
                y = pageHeight / 2;
            }

            pdf.setFont('Arimo', 'normal');
            pdf.setFontSize(32);
            pdf.setTextColor(44, 62, 80);
            pdf.text('Комерційна пропозиція', pdf.internal.pageSize.getWidth() / 2, y, { align: 'center' });

            pdf.addPage();
            y = 20;

            pdf.setFont('Arimo', 'normal');
            pdf.setFontSize(13);
            pdf.setTextColor(100, 116, 139);
            pdf.text(`Дата: ${kpData.date}`, pdf.internal.pageSize.getWidth() - leftMargin, y, { align: 'right' });
            y += 15;

            const detailTableBody = [];

            // Сначала определяем, какие лестницы нужно считать (floor1 и floor2 - одна лестница)
            const stairsToCount = new Set();
            const processedStairs = new Set();

            modulesForKP.forEach(module => {
                if (module.subtype === 'stairs' && module.templateId) {
                    const counterpart = STAIRS_COUNTERPART_MAP[module.templateId];

                    // Если эта лестница уже была обработана, пропускаем
                    if (processedStairs.has(module.templateId)) {
                        return;
                    }

                    // Если counterpart уже был обработан, значит эта лестница - вторая часть, пропускаем
                    if (counterpart && processedStairs.has(counterpart)) {
                        processedStairs.add(module.templateId);
                        return;
                    }

                    // Это первая встреча этой пары лестниц - добавляем в список для подсчета
                    stairsToCount.add(module.templateId);
                    processedStairs.add(module.templateId);
                    if (counterpart) {
                        processedStairs.add(counterpart);
                    }
                }
            });

            // Групуємо модулі за subtype, враховуючи індивідуальні ціни шаблонів
            const moduleGroups = {};
            modulesForKP.forEach(module => {
                // Для лестниц: проверяем, нужно ли считать эту лестницу
                if (module.subtype === 'stairs' && module.templateId) {
                    // Если эта лестница не в списке для подсчета, пропускаем её
                    if (!stairsToCount.has(module.templateId)) {
                        return; // Пропускаем вторую часть лестницы
                    }
                }

                const moduleKey = module.subtype || module.type;
                if (!moduleGroups[moduleKey]) {
                    moduleGroups[moduleKey] = { count: 0, totalPrice: 0 };
                }

                // Використовуємо ціну з шаблону, якщо є templateId, інакше загальну ціну за subtype
                const modulePrice = (module.templateId && templatePricesById[module.templateId])
                    ? templatePricesById[module.templateId]
                    : (currentPrices.MODULES[moduleKey] || 0);

                moduleGroups[moduleKey].count += 1;
                moduleGroups[moduleKey].totalPrice += modulePrice;
            });

            Object.entries(moduleGroups).forEach(([moduleKey, data]) => {
                const moduleName = t(`prices.modules.${moduleKey}`, { defaultValue: moduleKey });
                detailTableBody.push([
                    `${moduleName} (${data.count} шт.)`,
                    formatNumber(data.totalPrice)
                ]);
            });

            const doorInfo = { left: 0, right: 0, outward: 0, inward: 0 };

            if (projectState && projectState.projectObjects) {
                Object.values(projectState.projectObjects).forEach(floorObjects => {
                    floorObjects.forEach(obj => {
                        if (obj.type === 'module' && obj.wallSegments) {
                            Object.values(obj.wallSegments).forEach(segment => {
                                (segment.elements || []).forEach(element => {
                                    if (element.type === 'door') {
                                        const hingeSide = element.hingeSide || 'left';
                                        const openingDirection = element.openingDirection || 'outward';
                                        doorInfo[hingeSide]++;
                                        doorInfo[openingDirection]++;
                                    }
                                });
                            });
                        }
                        if (obj.type === 'docking_joint' && obj.elements) {
                            obj.elements.forEach(element => {
                                if (element.type === 'door') {
                                    const hingeSide = element.hingeSide || 'left';
                                    const openingDirection = element.openingDirection || 'outward';
                                    doorInfo[hingeSide]++;
                                    doorInfo[openingDirection]++;
                                }
                            });
                        }
                    });
                });
            }

            const totalDoorsCount = kpData.doors || 0;

            // Формируем динамическую информацию о стороне открытия
            let openingDirection = '';
            if (doorInfo.outward > 0 && doorInfo.inward > 0) {
                openingDirection = 'на зовнішню та внутрішню сторону';
            } else if (doorInfo.outward > 0) {
                openingDirection = 'на зовнішню сторону';
            } else if (doorInfo.inward > 0) {
                openingDirection = 'на внутрішню сторону';
            }

            // Формируем динамическую информацию о стороне петель
            let hingeSide = '';
            if (doorInfo.left > 0 && doorInfo.right > 0) {
                hingeSide = 'правосторонні/лівосторонні';
            } else if (doorInfo.left > 0) {
                hingeSide = 'лівосторонні';
            } else if (doorInfo.right > 0) {
                hingeSide = 'правосторонні';
            }

            const doorDescription = `Двері зовнішні - відкриття ${openingDirection}, ${hingeSide}, стальний короб з ущільнювачем по периметру дверей, двостороння обшивка, пофарбованим сталевим листом. Зовнішні розміри: 960 х 2050 мм фарбовані в колір на вибір Замовника.

Двері внутрішні - металопластикові, глухі, світловий пройм 800х2000 мм, одностулкові, імпост на висоті 800 мм, обидва пройми сендвіч-панель, врізаний замок, натискна ручка.

Загальна кількість ${totalDoorsCount} шт.`;

            const elementMapping = {
                'small': { key: 'window', name: 'Вікно стандартне' },
                'large': { key: 'panoramic_window', name: 'Вікно панорамне' },
                'doors': { key: 'door', name: 'Двері' },
                'single': { key: 'outlet', name: 'Розетка електрична одинарна' },
                'double': { key: 'outlet_double', name: 'Розетка електрична подвійна' },
                'switches': { key: 'switch', name: 'Вимикач освітлення' },
                'fixtures': { key: 'light_ceiling', name: 'Світильник LED 36W' },
                'heaters': { key: 'radiator', name: 'Обігрівач електричний' },
                'airConditioners': { key: 'ac', name: 'Кондиціонер' },
                'windowBlinds': { key: 'roller_shutter', name: 'Ролета' },
                'sinks': { key: 'hand_basin', name: 'Умивальник з підводкою' },
                'toilets': { key: 'toilet', name: 'Унітаз керамічний' },
                'waterHeaters': { key: 'water_heater', name: 'Бойлер електричний' },
                'showers': { key: 'shower', name: 'Душова кабіна' },
                'urinals': { key: 'urinal', name: 'Пісуар керамічний' },
                'partitions': { key: 'partition', name: 'Перегородка сантехнічна' },
                'showerDrains': { key: 'shower_drain', name: 'Душовий трап (злив)' },
                'beds': { key: 'bed', name: 'Ліжко металеве 1900×800' },
                'desks': { key: 'desk', name: 'Стіл письмовий' },
                'chairs': { key: 'chair', name: 'Стілець офісний' }
            };

            const elementCounts = {};
            modulesForKP.forEach(module => {
                if (module.elements) {
                    for (const category in module.elements) {
                        const categoryData = module.elements[category];
                        if (typeof categoryData === 'number') {
                            if (categoryData > 0 && elementMapping[category]) {
                                const mappedKey = elementMapping[category].key;
                                elementCounts[mappedKey] = (elementCounts[mappedKey] || 0) + categoryData;
                            }
                        } else if (typeof categoryData === 'object') {
                            for (const elemKey in categoryData) {
                                const quantity = categoryData[elemKey];
                                if (quantity > 0 && elementMapping[elemKey]) {
                                    const mappedKey = elementMapping[elemKey].key;
                                    elementCounts[mappedKey] = (elementCounts[mappedKey] || 0) + quantity;
                                }
                            }
                        }
                    }
                }
            });

            Object.entries(elementCounts).forEach(([elemKey, count]) => {
                const mappingEntry = Object.values(elementMapping).find(m => m.key === elemKey);
                const elementName = mappingEntry ? mappingEntry.name : elemKey;
                const elementPrice = currentPrices.ELEMENTS[elemKey] || 0;
                const totalPrice = elementPrice * count;
                detailTableBody.push([
                    `${elementName} (${count} шт.)`,
                    formatNumber(totalPrice)
                ]);
            });

            autoTable(pdf, {
                startY: y,
                head: [['Найменування', { content: 'Вартість, грн', styles: { halign: 'right' } }]],
                body: detailTableBody,
                theme: 'striped',
                headStyles: { fillColor: [199, 29, 59], font: 'Arimo', fontStyle: 'normal', fontSize: 14, cellPadding: 6 },
                bodyStyles: {
                    font: 'Arimo',
                    fontStyle: 'normal',
                    fontSize: 12,
                    cellPadding: 5
                },
                columnStyles: {
                    0: { cellWidth: 120, font: 'Arimo', fontStyle: 'normal' },
                    1: { cellWidth: 60, halign: 'right', font: 'Arimo', fontStyle: 'normal' }
                },
                styles: { font: 'Arimo', fontStyle: 'normal' },
                didDrawPage: (data) => { y = data.cursor.y; }
            });
            y += 10;

            const buildingDescription = `Будівля мобільна (інвентарна) контейнерна\n${kpData.buildingDimensions}`;

            const summaryTableBody = [[
                buildingDescription,
                formatNumber(totalCost)
            ]];

            autoTable(pdf, {
                startY: y,
                head: [['Найменування', { content: 'Вартість з ПДВ, грн', styles: { halign: 'right' } }]],
                body: summaryTableBody,
                theme: 'striped',
                headStyles: { fillColor: [199, 29, 59], font: 'Arimo', fontStyle: 'normal', fontSize: 14, cellPadding: 6 },
                bodyStyles: {
                    font: 'Arimo',
                    fontStyle: 'normal',
                    fontSize: 13,
                    cellPadding: 8,
                    valign: 'middle'
                },
                columnStyles: {
                    0: { cellWidth: 120, font: 'Arimo', fontStyle: 'normal' },
                    1: { cellWidth: 60, halign: 'right', font: 'Arimo', fontStyle: 'normal', textColor: [199, 29, 59], fontStyle: 'bold', fontSize: 15 }
                },
                styles: { font: 'Arimo', fontStyle: 'normal' },
                didDrawPage: (data) => { y = data.cursor.y; }
            });

            pdf.addPage();
            y = 20;

            pdf.setFont('Arimo', 'normal');
            pdf.setFontSize(18);
            pdf.setTextColor(44, 62, 80);
            pdf.text('Технічні характеристики', pdf.internal.pageSize.getWidth() / 2, y, { align: 'center' });
            y += 15;

            const getRoofDescription = () => {
                let descriptions = [];
                if (kpData.hasResidential) {
                    descriptions.push("Житловий: цільний готовий конструктивний елемент: ззовні –оцинкований сталевий лист товщиною від 0,45 мм, подвійний фальц, гідроізоляційна плівка, OSB плита товщиною 10 мм, утеплення мінеральною ватою товщиною не менше 100 мм, паробар'єр, всередині – ламінований ДСП не менше 10 мм.");
                }
                if (kpData.hasSanitary) {
                    descriptions.push("Санітарний: цільний готовий конструктивний елемент: ззовні – оцинкований сталевий лист товщиною від 0,45 мм, подвійний фальц, гідроізоляційна плівка, OSB плита товщиною 10 мм, утеплення мінеральною ватою товщиною не менше 100 мм, паробар'єр, всередині – профільний лист П-8 з полімерним покриттям товщиною 0.45 мм, колір RAL 9003 (білий).");
                }
                if (descriptions.length === 0) return "Стандартна.";
                return descriptions.join('\n\n');
            };

            const moduleListText = Object.entries(moduleGroups)
                .map(([moduleKey, data]) => {
                    let moduleName = t(`prices.modules.${moduleKey}`, { defaultValue: moduleKey });
                    moduleName = moduleName.toLowerCase().replace('модуль', '');
                    if (data.count === 1) {
                        return `${data.count} ${moduleName}модуль`;
                    } else if (data.count >= 2 && data.count <= 4) {
                        return `${data.count} ${moduleName}модулі`;
                    } else {
                        return `${data.count} ${moduleName}модулів`;
                    }
                })
                .join(', ');

            // Проверяем наличие санитарных модулей
            const hasSanitaryModules = (moduleGroups['toilet']?.count > 0) ||
                (moduleGroups['shower']?.count > 0) ||
                (moduleGroups['sanitary']?.count > 0) ||
                (moduleGroups['sanitary_combined']?.count > 0);

            let techSpecBody = [
                ['Будівля мобільна (інвентарна) контейнерна', `${kpData.buildingDimensions}. Модель: Стандарт`],
                ['Каркас', `Збірно-розбірна конструкція, що складається з ${kpData.moduleCount} модулів, з'єднаних між собою гвинтовими стяжками.\n\nЗовнішні розміри модулів:\n${kpData.moduleDimensionsList.replace(/<br>/g, '\n')}\n\nКонструкція рами модуля: збірно-розбірна конструкція.\n\nКонструкція рами основи модуля: зварна із гнутого пресом цільного сталевого швелера не менше ніж 160х100х60х4мм, або із зварного сталевого замкнутого профілю холодного прокату, 4 кути модуля зварні (сталевий лист товщиною 5 мм), оснащені нішами для перевезення (такелажними кріпленнями). Рама основи має посилення за допомогою лаг – гнутий пресом швелер (сталевий лист товщиною 3 мм).\n\nКонструкція рами покриття модуля: зварна із гнутого пресом цільного сталевого спец профілю висотою 190мм, 4 кути модуля зварні (сталевий лист товщиною 5 мм), оснащені нішами для перевезення (такелажними кріпленнями).\n\nКонструкція посилена елементами жорсткості – гнутий пресом спец профіль (сталевий лист товщиною 3 мм), для запобігання провисанню повздовжніх балок рами.\n\nКутові стійки модуля: зварні із сталевого гнутого пресом профілю (сталевий лист товщиною 4 мм).\n\nКонструктивні елементи рами поєднані між собою болтовим з'єднанням.\n\nКаркасна система пофарбована в колір на вибір замовника.`],
                ['Покрівля', getRoofDescription()],
                ['Зовнішні стіни', 'Сендвіч-панелі, що складаються із: металева плита з полімерним покриттям товщиною 0,45 мм, колір на вибір замовника, наповнення з ПІР матеріалу товщиною 100 мм, зсередини металева плита з полімерним покриттям товщиною 0,45 мм, колір RAL 9003.'],
                ['Основа', 'Цільний готовий конструктивний елемент, що складається із: підшитого оцинкованого гладкого листа (товщина металу 1 мм), гідроізоляційної плівки, утеплення мінеральною ватою 100 мм, паробар\'єру, цементностружкової плити товщиною 20 мм, ПВХ покриття для підлоги товщиною 2 мм – звареного на з\'єднаннях.'],
                ['Двері', doorDescription],
                ['Вікно', `ПВХ з подвійним склопакетом з газовим наповненням, внутрішній колір білий, зовнішній колір білий. Фурнітура похило-поворотна. ${kpData.windowsDescription}`],
                ...(hasSanitaryModules ? [['Вікно санітарне', 'PVC вікна з імпостом та металевою притискною планкою. Поворотно-відкидний механізм, зовнішні розміри: 645 х 445 мм (h)мм. На замовлення.']] : []),
                ['Підключення', 'Втоплений в раму зовнішній СЕЕ-роз\'єм (вилка/розетка) 32A, IP44.'],
                ['Система заземлення', 'TN-S (згідно з ГОСТ Р 30339).'],
                ['Живильна лінія', 'Система електропроводки – внутрішня. Провід прокладається в контейнера, кабель канал пластикова гофрована труба d20 або метало рукав d20, або зовнішній кабель-канал який відповідає ПУЕ. З даху провод опускається до електричних точок. Кабель ПВС 5х6. Клас напруги: 400В, 3P+N+PE, 32 A1 / 2 / 3.'],
                ['Захист на введенні', 'Автомат 3п 40 А С плюс УЗО 4 п 40А 30 мА С, які встановлюються у груповому щитку.'],
                ['Груповий щиток', 'Накладний, що відповідає ПУЕ.'],
                ['Групові лінії', 'Кабель ВВГНГ-LS.'],
                ['Освітлення', `Автоматичний вимикач 10А, характеристика відключення автомата С, 1-полюсний (кабель 3х1,5). ${kpData.lightingDescription}`],
                ['Опалення', 'Автоматичний вимикач 16A, Ik.'],
                ['Розетки', `Автоматичний вимикач 16А, характеристика відключення автомата С, 1-полюсний (кабель 3х1,5). ${kpData.socketsDescription}`],
                ['Слаботочні системи', 'В конструкції модуля передбачені отвори та місця для прокладання кабелів зв\'язку, систем оповіщення та інших слаботочних систем у внутрішній дверній конструкції – лівому нижньому куті D50 мм із пластиковими заглушками.'],
                ['Кліматична система', `Автоматичний вимикач 16А, характеристика відключення автомата С, 1-полюсний (кабель 3х2,5). ${kpData.climateDescription}`],
                ['Заземлення', 'Клема заземлення універсальна: у рамі підлоги з обох торцевих сторін по кутах передбачені отвори Ø 10 мм для кріплення клеми заземлення.'],
                ['Сантехнічне обладнання', `Трубопровід води виконаний пластиковим матеріалом на спайці, два кулькових крани. Каналізаційний трубопровід виконаний пластиковою трубою 50-100 d 14. ${kpData.plumbingDescription}`],
                ['Меблі', (() => {
                    const furnitureDesc = kpData.furnitureDescription || 'Відсутнє.';
                    console.log('📋 [CostPanel] Формирование строки Меблі:');
                    console.log('  furnitureDescription из kpData:', furnitureDesc);
                    console.log('  furnitureDescription length:', furnitureDesc.length);
                    return furnitureDesc;
                })()],
                ['Додаткове обладнання', kpData.jacksDescription],
                ['Гарантійний термін експлуатації', '24 місяці.'],
            ];

            // Use dynamic template from Admin Panel if available
            if (kpSettings && kpSettings.technicalTemplate && kpSettings.technicalTemplate.length > 0) {
                const templateContext = {
                    ...kpData,
                    roofDescription: getRoofDescription(),
                    doorsFullDescription: doorDescription
                };
                techSpecBody = processTemplateRows(kpSettings.technicalTemplate, templateContext);
            }

            // FORCE REFRESH AND OVERRIDE AGAIN (Fix for admin panel update delay)
            let dynamicTemplate2 = null;
            try {
                const sRes2 = await kpService.getSettings('standard');
                if (sRes2.data?.success) {
                    let t = sRes2.data.data.technicalTemplate;
                    if (typeof t === 'string') try { t = JSON.parse(t); } catch (e) { /* ignore */ }
                    if (Array.isArray(t) && t.length > 0) dynamicTemplate2 = t;
                }
            } catch (e) { /* ignore */ }

            if (dynamicTemplate2) {
                const templateContext2 = {
                    ...kpData,
                    roofDescription: getRoofDescription(),
                    doorsFullDescription: doorDescription
                };
                techSpecBody = processTemplateRows(dynamicTemplate2, templateContext2);
            }

            autoTable(pdf, {
                startY: y,
                head: [['Характеристика', 'Опис']],
                body: techSpecBody,
                theme: 'striped',
                headStyles: { fillColor: [199, 29, 59], font: 'Arimo', fontStyle: 'normal', fontSize: 14, cellPadding: 6 },
                bodyStyles: { font: 'Arimo', fontStyle: 'normal', fontSize: 11, cellPadding: 5, valign: 'top', halign: 'justify' },
                columnStyles: {
                    0: { cellWidth: 60, font: 'Arimo', fontStyle: 'bold', halign: 'left' },
                    1: { font: 'Arimo', fontStyle: 'normal', halign: 'justify' }
                },
                styles: { font: 'Arimo', fontStyle: 'normal', lineColor: [220, 220, 220], lineWidth: 0.5 },
                didDrawPage: (data) => { y = data.cursor.y; }
            });

            const floorImages = await generateLayoutImagesForAllFloors();
            if (floorImages.length > 0) {
                for (const { imageData, floorName } of floorImages) {
                    if (!imageData) continue;

                    pdf.addPage();
                    const topMargin = 35;
                    const bottomMargin = 35;
                    const pageW = pdf.internal.pageSize.getWidth();
                    const pageH = pdf.internal.pageSize.getHeight();

                    pdf.setFont('Arimo', 'normal');
                    pdf.setFontSize(16);
                    pdf.setTextColor(44, 62, 80);
                    const title = floorImages.length > 1 ? `План-схема будівлі - ${floorName}` : 'План-схема будівлі';
                    pdf.text(title, pageW / 2, 25, { align: 'center' });

                    const availableWidth = pageW - leftMargin * 2;
                    const availableHeight = pageH - topMargin - bottomMargin;

                    const imgProps = pdf.getImageProperties(imageData);
                    const imageAspectRatio = imgProps.width / imgProps.height;
                    const availableSpaceAspectRatio = availableWidth / availableHeight;

                    let finalImgWidth, finalImgHeight;

                    if (imageAspectRatio > availableSpaceAspectRatio) {
                        finalImgWidth = availableWidth;
                        finalImgHeight = finalImgWidth / imageAspectRatio;
                    } else {
                        finalImgHeight = availableHeight;
                        finalImgWidth = finalImgHeight * imageAspectRatio;
                    }

                    const centerX = (pageW - finalImgWidth) / 2;
                    const centerY = topMargin + (availableHeight - finalImgHeight) / 2;

                    pdf.addImage(imageData, 'PNG', centerX, centerY, finalImgWidth, finalImgHeight);
                }
            }

            pdf.save(`КП_${kpData.projectName.replace(/\s/g, '_')}_${kpData.date}.pdf`);
            toast.success('PDF з редагованим текстом успішно згенеровано!');
        } catch (error) {
            console.error('❌ Ошибка при генерации PDF:', error);
            console.error('  error.response:', error.response);
            console.error('  error.response?.data:', error.response?.data);
            console.error('  error.message:', error.message);

            const serverMessage = error.response?.data?.message || error.message || 'Невідома помилка';
            toast.error(`Помилка генерації PDF: ${serverMessage}`);
        } finally {
            setIsGenerating(false);
        }
    };


    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                className="bg-white/80 backdrop-blur-sm border border-gray-300 rounded-lg shadow-xl p-4 w-64 z-20"
            >
                <div className="flex justify-between items-baseline border-b border-gray-200 pb-2 mb-2">
                    <h3 className="font-semibold text-gray-700">{t('cost.total')}</h3>
                    <p className="text-lg font-bold text-primary-blue">{formatPrice(totalCost)}</p>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                    <div className="flex justify-between">
                        <span>{costBreakdown.modules.label}</span>
                        <span>{formatPrice(costBreakdown.modules.total)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>{costBreakdown.elements.label}</span>
                        <span>{formatPrice(costBreakdown.elements.total)}</span>
                    </div>
                </div>
                <div className="mt-3 pt-2 border-t border-gray-200">
                    <button
                        onClick={handleGenerateKP}
                        disabled={isGenerating || !canGenerateKP()}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-blue text-white text-sm font-medium rounded-md hover:bg-hover-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={!canGenerateKP() && projectState?.activeMode === 'frameless' ? 'Неможливо створити КП: проект не є закритою кімнатою' : ''}
                    >
                        {isGenerating ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>Генерація...</span>
                            </>
                        ) : (
                            <>
                                <DocumentArrowDownIcon className="w-4 h-4" />
                                <span>КП (PDF)</span>
                            </>
                        )}
                    </button>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default CostPanel;